<template>
    <section class="flex flex-col gap-5">
        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("common.back") }} </BaseButton>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="results">
            <header class="flex items-start justify-between gap-4 flex-wrap">
                <div class="flex flex-col gap-1 min-w-0">
                    <h2 class="title-subsection text-ink">{{ results.template.name }}</h2>
                    <I18nT keypath="reflectionsAdmin.individual360Report.subtitle" tag="p" class="body-small text-ink-muted">
                        <template #participants>
                            <span class="body-small-bold text-ink">{{ t("reflectionsAdmin.individual360Report.participantsCount", { count: results.participant_count }) }}</span>
                        </template>
                        <template #submitted>{{ results.submitted_count }}</template>
                    </I18nT>
                    <p v-if="report?.generated_at" class="details text-ink-muted">
                        {{ t("reflectionsAdmin.campaign.reportGenerated", { date: formatDate(report.generated_at, "short") }) }}
                    </p>
                    <p v-if="report && !results.can_generate" class="details text-ink-muted">{{ t("reflectionsAdmin.individual360Report.upToDate") }}</p>
                </div>
                <div class="flex items-center gap-2 flex-wrap">
                    <BaseButton theme="neutral" size="sm" :disabled="downloading || results.submitted_count < results.min_participants" @click="onDownloadCsv">
                        <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                        {{ downloadingCsv ? t("reflectionsAdmin.individual360s.exporting") : t("reflections.campaignReport.downloadCsv") }}
                    </BaseButton>
                    <BaseButton theme="neutral" size="sm" :disabled="downloading || !report" @click="onDownloadPdf">
                        <i class="bi bi-download" aria-hidden="true"></i>
                        {{ downloadingPdf ? t("reflectionsAdmin.individual360s.exporting") : t("reflections.campaignReport.downloadPdf") }}
                    </BaseButton>
                    <p role="status" class="body-small text-status-error empty:sr-only">{{ exportError }}</p>
                    <BaseButton theme="primary" size="sm" :disabled="regenerating || !results.can_generate" @click="onRegenerate">
                        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                        {{ regenerateLabel }}
                    </BaseButton>
                </div>
            </header>

            <BaseNotification v-if="regenStatus" :theme="regenStatus.theme" :text="regenStatus.text" @dismiss="regenStatus = null" />

            <HorizontalDivider as="hr" spacing="none" />

            <BaseCard v-if="!report" padding="lg" class="text-center">
                <p class="body-small text-ink-muted">
                    {{
                        results.participant_count < results.min_participants
                            ? t("reflectionsAdmin.individual360Report.tooFewParticipants", { count: results.min_participants })
                            : regenerating
                              ? t("reflectionsAdmin.individual360Report.generating")
                              : t("reflectionsAdmin.individual360Report.noReport")
                    }}
                </p>
            </BaseCard>

            <PooledReportBody v-else :report="report" />
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CampaignReportPayload, Individual360ResultsResponse } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PooledReportBody from "~vue/pages/reflections/PooledReportBody.vue";
import { useReportRegeneration } from "~vue/pages/reflections/useReportRegeneration";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { downloadApiFile } from "~vue/utils/downloadApiFile";
import { exportErrorCode, MIN_INDIVIDUAL_REPORT_PARTICIPANTS, TOO_FEW_PARTICIPANTS_CODE } from "~vue/utils/exportErrorCode";
import { logUserInteraction } from "~vue/utils/logUtils";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

interface Props {
    templateId: string;
}

const LIST_PATH = "/v1/reflections/admin/individual-360s";

const props = defineProps<Props>();
const emit = defineEmits<{ (event: "back"): void }>();
const { t } = useI18n();

const results = ref<Individual360ResultsResponse | null>(null);
const report = computed<CampaignReportPayload | null>(() => results.value?.report ?? null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const downloadingCsv = ref(false);
const downloadingPdf = ref(false);
// One message for the whole action row, so only one download may run at a time:
// otherwise a slower earlier failure could overwrite a newer attempt's result.
const downloading = computed(() => downloadingCsv.value || downloadingPdf.value);
const exportError = ref<string | null>(null);

// The CSV pools every submitted individual 360 on the template, open or
// closed; the export itself holds back rater averages for cycles still open.
const filterParams = computed(() => new URLSearchParams({ state: "submitted", report: "all", status: "all", template_id: props.templateId }));
const reportParams = computed(() => new URLSearchParams({ template_id: props.templateId }));

async function fetchResults(): Promise<Individual360ResultsResponse | null> {
    const { data } = await useFetch(`${LIST_PATH}/report?${reportParams.value}`).json<Individual360ResultsResponse>();
    return data.value ?? null;
}

const { regenerating, regenStatus, regenerate } = useReportRegeneration<Individual360ResultsResponse>({
    fetchResults,
    generatedAt: (latest) => latest?.report?.generated_at ?? null,
    queueRegeneration: async () => {
        const { error: postError } = await useFetch(`${LIST_PATH}/report/regenerate?${reportParams.value}`, { method: "POST" }).json();
        return !postError.value;
    },
    onRegenerated: (latest) => {
        results.value = latest;
    },
});

const regenerateLabel = computed(() => {
    if (regenerating.value) {
        return t("reflections.campaignReport.queued");
    }
    return report.value ? t("reflectionsAdmin.individual360Report.refresh") : t("reflectionsAdmin.individual360Report.generate");
});

function onRegenerate() {
    void regenerate(report.value?.generated_at ?? null);
}

async function onDownloadCsv() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_INDIVIDUAL_360S_SCORES_CSV_DOWNLOAD_CLICKED, { template_id: props.templateId });
    downloadingCsv.value = true;
    exportError.value = null;
    try {
        const downloaded = await downloadApiFile(`${LIST_PATH}/export-scores-csv?${filterParams.value}`, "individual_360s_scores.csv", async (response) => {
            exportError.value =
                (await exportErrorCode(response)) === TOO_FEW_PARTICIPANTS_CODE
                    ? t("reflectionsAdmin.individual360s.exportTooFewParticipants", { count: MIN_INDIVIDUAL_REPORT_PARTICIPANTS })
                    : t("reflectionsAdmin.individual360s.exportFailed");
        });
        if (!downloaded) {
            exportError.value ??= t("reflectionsAdmin.individual360s.exportFailed");
        }
    } catch {
        exportError.value = t("reflectionsAdmin.individual360s.exportFailed");
    } finally {
        downloadingCsv.value = false;
    }
}

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    downloadingPdf.value = true;
    exportError.value = null;
    try {
        const downloaded = await downloadApiFile(`${LIST_PATH}/report/export-pdf?${reportParams.value}`, "individual_360s_report.pdf");
        if (!downloaded) {
            exportError.value = t("reflectionsAdmin.individual360s.pdfExportFailed");
        }
    } catch {
        exportError.value = t("reflectionsAdmin.individual360s.pdfExportFailed");
    } finally {
        downloadingPdf.value = false;
    }
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`${LIST_PATH}/report?${reportParams.value}`).json<Individual360ResultsResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || t("reflectionsAdmin.individual360Report.loadFailed");
        return;
    }
    results.value = data.value ?? null;
    // Nothing rebuilds this report on a schedule: the first open with enough
    // reported 360s builds it, later opens only offer a refresh when due.
    if (results.value && !results.value.report && results.value.can_generate) {
        void regenerate(null);
    }
});
</script>
